import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Ticket } from "lucide-react";
import adminApi, { apiError } from "@/lib/adminApi";

const inputClass =
  "bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-gold outline-none w-full";

const EMPTY = { code: "", discount_percent: 100, unlimited: true, max_uses: 1, is_active: true };

const AdminCodesPromo = () => {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => adminApi.get("/promo-codes").then(({ data }) => setCodes(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const payload = () => ({
    code: form.code,
    discount_percent: Number(form.discount_percent),
    max_uses: form.unlimited ? null : Number(form.max_uses),
    is_active: form.is_active,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) {
      toast.error("Saisissez un code (ex : PARRAIN10).");
      return;
    }
    setSaving(true);
    try {
      if (editingId) await adminApi.put(`/promo-codes/${editingId}`, payload());
      else await adminApi.post("/promo-codes", payload());
      toast.success(editingId ? "Code mis à jour." : "Code promo créé.");
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await adminApi.put(`/promo-codes/${c.id}`, {
        code: c.code, discount_percent: c.discount_percent, max_uses: c.max_uses, is_active: !c.is_active,
      });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (c) => {
    try {
      await adminApi.delete(`/promo-codes/${c.id}`);
      toast.success("Code promo supprimé.");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="admin-promo-codes" className="space-y-6">
      <h1 className="font-display uppercase text-4xl tracking-wide">Codes promo</h1>
      <p className="text-sm text-white/50">
        Les familles saisissent le code dans le formulaire d'inscription. À 100 % de réduction, l'inscription est
        confirmée immédiatement sans paiement — l'email de confirmation est envoyé comme d'habitude.
      </p>

      <form onSubmit={submit} data-testid="promo-form" className="bg-noir-surface border border-noir-border p-6 space-y-4">
        <h2 className="font-display uppercase text-xl tracking-wide text-gold">
          {editingId ? "Modifier le code" : "Créer un code"}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-white/50 mb-2">Code *</label>
            <input data-testid="promo-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="Ex : PARRAIN100" className={`${inputClass} uppercase`} />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-white/50 mb-2">Réduction %</label>
            <input data-testid="promo-discount-input" type="number" min="1" max="100" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className={inputClass} />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-white/50 mb-2">Utilisations max</label>
            <div className="flex items-center gap-3">
              <input data-testid="promo-maxuses-input" type="number" min="1" disabled={form.unlimited} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className={`${inputClass} ${form.unlimited ? "opacity-40" : ""}`} />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-3 text-sm text-white/70 cursor-pointer">
            <input data-testid="promo-unlimited-check" type="checkbox" checked={form.unlimited} onChange={(e) => setForm({ ...form, unlimited: e.target.checked })} className="w-4 h-4 accent-[#D4AF37]" />
            Illimité
          </label>
          <label className="flex items-center gap-3 text-sm text-white/70 cursor-pointer">
            <input data-testid="promo-active-check" type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 accent-[#D4AF37]" />
            Actif
          </label>
        </div>
        <div className="flex gap-3">
          <button data-testid="promo-submit" disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 bg-rouge text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {editingId ? "Enregistrer" : "Créer le code"}
          </button>
          {editingId && (
            <button type="button" data-testid="promo-cancel-edit" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="px-6 py-3 border border-white/20 text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:border-white">
              Annuler
            </button>
          )}
        </div>
      </form>

      <div className="space-y-3">
        {codes.map((c) => (
          <div key={c.id} data-testid={`promo-row-${c.id}`} className="bg-noir-surface border border-noir-border p-4 flex flex-wrap items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center border border-gold/30 bg-black/40">
              <Ticket size={16} className="text-gold" />
            </div>
            <div className="flex-1 min-w-[180px]">
              <p className="font-semibold text-sm tracking-wider">{c.code} <span className="text-gold">−{c.discount_percent} %</span></p>
              <p className="text-xs text-white/40">
                Utilisations : {c.used_count}{c.max_uses ? ` / ${c.max_uses}` : " / illimité"}
                {c.max_uses && c.used_count >= c.max_uses && <span className="text-rouge-hover font-bold"> — épuisé</span>}
              </p>
            </div>
            <button
              data-testid={`promo-toggle-${c.id}`}
              onClick={() => toggleActive(c)}
              className={`text-[10px] font-bold uppercase tracking-[0.15em] border px-3 py-1.5 transition-colors ${
                c.is_active ? "text-gold border-gold/40" : "text-white/40 border-white/15"
              }`}
            >
              {c.is_active ? "Actif" : "Inactif"}
            </button>
            <button
              data-testid={`promo-edit-${c.id}`}
              onClick={() => { setEditingId(c.id); setForm({ code: c.code, discount_percent: c.discount_percent, unlimited: !c.max_uses, max_uses: c.max_uses || 1, is_active: c.is_active }); window.scrollTo({ top: 0 }); }}
              className="px-4 py-2 border border-white/15 text-white/60 hover:border-gold hover:text-gold text-xs font-bold uppercase tracking-wider transition-colors"
            >
              Modifier
            </button>
            <button data-testid={`promo-delete-${c.id}`} onClick={() => setConfirmDelete(c)} className="w-9 h-9 flex items-center justify-center border border-white/15 text-white/60 hover:border-rouge hover:text-rouge-hover transition-colors">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {codes.length === 0 && <p className="text-white/40 text-sm">Aucun code promo pour le moment.</p>}
      </div>

      {confirmDelete && (
        <div data-testid="promo-delete-confirm" className="fixed inset-0 z-50 bg-noir/90 backdrop-blur flex items-center justify-center p-6">
          <div className="bg-noir-surface border border-rouge/40 p-8 max-w-md w-full">
            <h2 className="font-display uppercase text-2xl tracking-wide text-rouge-hover">Supprimer ce code ?</h2>
            <p className="mt-3 text-white/60 text-sm">Le code <strong className="text-white">{confirmDelete.code}</strong> ne fonctionnera plus. Les inscriptions déjà faites avec ce code ne sont pas affectées.</p>
            <div className="mt-6 flex gap-3">
              <button data-testid="promo-delete-yes" onClick={() => remove(confirmDelete)} className="px-6 py-3 bg-rouge text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover">
                Supprimer
              </button>
              <button data-testid="promo-delete-no" onClick={() => setConfirmDelete(null)} className="px-6 py-3 border border-white/20 text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:border-white">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCodesPromo;
